import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { ne } from '@/i18n/ne'
import { setNepaliDigits } from './format'

/**
 * English and Nepali. Strings are written in English in the code and used as
 * keys; i18n/ne.ts maps them to Nepali. A missing translation falls back to
 * English, so screens never break while translations catch up.
 *
 *   t('Charging at {station}', { station })
 */
export type Lang = 'en' | 'ne'

interface I18n {
  lang: Lang
  setLang: (l: Lang) => void
  t: (text: string, vars?: Record<string, string | number>) => string
}

const Ctx = createContext<I18n | null>(null)
const KEY = 'saadhan.lang'

export function interpolate(text: string, vars?: Record<string, string | number>) {
  if (!vars) return text
  return text.replace(/\{(\w+)\}/g, (_, k) => (vars[k] === undefined ? `{${k}}` : String(vars[k])))
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    try {
      return localStorage.getItem(KEY) === 'ne' ? 'ne' : 'en'
    } catch {
      return 'en'
    }
  })
  // Formatters read this synchronously, so it is set during render (idempotent).
  setNepaliDigits(lang === 'ne')
  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])
  const setLang = useCallback((l: Lang) => {
    setLangState(l)
    try {
      localStorage.setItem(KEY, l)
    } catch {
      /* ignore */
    }
  }, [])
  const t = useCallback(
    (text: string, vars?: Record<string, string | number>) => interpolate(lang === 'ne' ? (ne[text] ?? text) : text, vars),
    [lang],
  )
  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useI18n() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useI18n outside I18nProvider')
  return ctx
}
